import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Alert, Box, Button, CircularProgress, FormControl, InputLabel, MenuItem, Select, Stack, Typography } from "@mui/material";

const fieldSx = {
  minWidth: 0, flex: 1,
  "& .MuiInputLabel-root, & .MuiSelect-icon": { color: "var(--iio-text-2)" },
  "& .MuiOutlinedInput-root": { color: "var(--iio-text-1)", background: "var(--iio-bg-1)", fontFamily: "inherit" },
  "& .MuiOutlinedInput-notchedOutline": { borderColor: "var(--iio-border)" },
  "& .Mui-focused .MuiOutlinedInput-notchedOutline": { borderColor: "var(--iio-info)" },
};
const buttonSx = { fontFamily: "inherit", textTransform: "none", alignSelf: "flex-start", color: "var(--iio-info)", borderColor: "var(--iio-border)", minHeight: 44 };
const roles = ["content", "actions"];
const validRepo = repo => repo && /^\d+$/.test(String(repo.id)) && typeof repo.fullName === "string" && repo.fullName.includes("/");

function validateDiscovery(result) {
  if (!result || !["resolved", "selection_required"].includes(result.status) || !result.projectRevision) return false;
  return roles.every(role => validRepo(result.repositories?.[role]) || (
    result.status === "selection_required" && Array.isArray(result.candidates?.[role]) &&
    result.candidates[role].length > 0 && result.candidates[role].every(validRepo)
  ));
}

/** Identity discovery is scoped to the mounted Space and never guesses an ambiguous role. */
export default function RepositoryZuordnung({ api, spaceId, onSaved }) {
  const { t } = useTranslation("cicdsetup");
  const tRef = useRef(t);
  const savedRef = useRef(onSaved);
  tRef.current = t;
  savedRef.current = onSaved;
  const generation = useRef(0);
  const activeSave = useRef(false);
  const [retry, setRetry] = useState(0);
  const [state, setState] = useState({ loading: true });
  const [selection, setSelection] = useState({ content: "", actions: "" });

  useEffect(() => {
    const request = ++generation.current;
    let active = true;
    const current = () => active && request === generation.current;
    setState({ loading: true });
    setSelection({ content: "", actions: "" });
    activeSave.current = false;
    (async () => {
      try {
        const result = await api(`/api/cicd/repository-identity?spaceId=${encodeURIComponent(spaceId)}`);
        if (!current()) return;
        if (!validateDiscovery(result)) throw new Error(tRef.current("repositoryIdentity.invalidResponse"));
        setSelection(Object.fromEntries(roles.map(role => [role, String(result.repositories?.[role]?.id || (result.candidates?.[role]?.length === 1 ? result.candidates[role][0].id : ""))])));
        if (result.status === "resolved" && result.needsConfirmation === true) {
          setState({ result, saving: true });
          const saved = await api("/api/cicd/repository-identity", {
            method: "POST", body: JSON.stringify({ spaceId, expectedRevision: result.projectRevision, selection: {} }),
          });
          if (!current()) return;
          if (!saved?.ok) throw new Error(tRef.current("repositoryIdentity.invalidResponse"));
          setState({ result, saved: !saved.deferred, deferred: !!saved.deferred });
          if (!saved.deferred) savedRef.current?.();
        } else {
          setState({ result, saved: result.status === "resolved" });
        }
      } catch (error) {
        if (current()) setState({ error: error.message || tRef.current("repositoryIdentity.failed") });
      }
    })();
    return () => { active = false; generation.current++; };
  }, [api, spaceId, retry]);

  async function save() {
    if (activeSave.current || !state.result || !roles.every(role => selection[role])) return;
    const request = generation.current;
    activeSave.current = true;
    setState(previous => ({ ...previous, saving: true, error: null }));
    try {
      const result = await api("/api/cicd/repository-identity", {
        method: "POST", body: JSON.stringify({ spaceId, expectedRevision: state.result.projectRevision,
          selection: { contentRepositoryId: selection.content, actionsRepositoryId: selection.actions } }),
      });
      if (request !== generation.current) return;
      if (!result?.ok) throw new Error(tRef.current("repositoryIdentity.invalidResponse"));
      setState(previous => ({ ...previous, saving: false, saved: !result.deferred, deferred: !!result.deferred }));
      if (!result.deferred) savedRef.current?.();
    } catch (error) {
      if (request === generation.current) setState(previous => ({ ...previous, saving: false, error: error.message || tRef.current("repositoryIdentity.failed") }));
    } finally {
      if (request === generation.current) activeSave.current = false;
    }
  }

  return (
    <Box component="section" aria-label={t("repositoryIdentity.title")} sx={{ my: 2, p: 2, border: "1px solid var(--iio-border)", borderRadius: "8px", color: "var(--iio-text-1)", background: "var(--iio-bg-1)" }}>
      <Typography component="h3" sx={{ fontFamily: "inherit", fontSize: 15, fontWeight: 600, mb: 1 }}>{t("repositoryIdentity.title")}</Typography>
      {(state.loading || state.saving) && <Stack direction="row" spacing={1} alignItems="center" role="status" aria-busy="true">
        <CircularProgress size={16} sx={{ color: "var(--iio-info)" }} aria-hidden="true"/>
        <span>{t(state.saving ? "repositoryIdentity.saving" : "repositoryIdentity.loading")}</span>
      </Stack>}
      {state.error && <Alert severity="error" sx={{ fontFamily: "inherit", color: "var(--iio-text-1)", background: "var(--iio-bg-2)", mb: 1 }}>{state.error}</Alert>}
      {state.saved && <Typography role="status" sx={{ fontFamily: "inherit", fontSize: 14 }}>{t("repositoryIdentity.saved")}</Typography>}
      {state.deferred && <Typography role="status" sx={{ fontFamily: "inherit", fontSize: 14 }}>{t("repositoryIdentity.deferred")}</Typography>}
      {state.result && !state.saved && !state.deferred && !state.saving && <>
        <Typography sx={{ fontFamily: "inherit", fontSize: 14, color: "var(--iio-text-2)", mb: 2 }}>{t("repositoryIdentity.choose")}</Typography>
        <Stack spacing={2} direction={{ xs: "column", md: "row" }}>
          {roles.map(role => {
            const bound = state.result.repositories?.[role];
            const candidates = bound ? [bound] : state.result.candidates[role];
            return <FormControl key={role} fullWidth size="small" sx={fieldSx} disabled={!!bound || candidates.length === 1}>
              <InputLabel id={`repository-${role}-label`}>{t(`repositoryIdentity.${role}`)}</InputLabel>
              <Select labelId={`repository-${role}-label`} label={t(`repositoryIdentity.${role}`)} value={selection[role]}
                onChange={event => setSelection(previous => ({ ...previous, [role]: event.target.value }))}
                MenuProps={{ PaperProps: { className: "iio", sx: { background: "var(--iio-bg-1)", color: "var(--iio-text-1)", fontFamily: "inherit" } } }}>
                {candidates.map(repo => <MenuItem key={repo.id} value={String(repo.id)} sx={{ fontFamily: "inherit", whiteSpace: "normal", overflowWrap: "anywhere" }}>{repo.fullName}</MenuItem>)}
              </Select>
            </FormControl>;
          })}
        </Stack>
        <Button variant="outlined" sx={{ ...buttonSx, mt: 2 }} disabled={!roles.every(role => selection[role])} onClick={save}>{t("repositoryIdentity.confirm")}</Button>
      </>}
      {state.error && <Button variant="outlined" onClick={() => setRetry(value => value + 1)} sx={buttonSx}>{t("repositoryIdentity.retry")}</Button>}
    </Box>
  );
}
